import { useAtom } from "jotai";
import { useAV } from "../av/useAV";
import { DeviceControl } from "../av/useDeviceControl";
import { Button, SegmentedButtonWrapper } from "../components/Button";
import Icon from "../components/Icon";
import { Menu, MenuItem, MenuLabel } from "../components/Menu";
import { currentUserAtom } from "../jotai/atoms";
import { styled } from "../stitches.config";

export default function AVControls() {
  const [currentUser] = useAtom(currentUserAtom);
  const { audio, video } = useAV();

  if (!currentUser) return null;

  return (
    <div style={{ display: "flex" }}>
      <DeviceButton device={video} />
      <DeviceButton device={audio} />
    </div>
  );
}

function DeviceButton({ device }: { device: DeviceControl }) {
  const {
    type,
    status,
    start,
    stop,
    deviceOptions,
    activeDevice,
    preferredDevice,
  } = device;

  const on = status === "on" || status === "stopping";

  const selected = deviceOptions?.find(({ deviceId }, index) => {
    if (activeDevice) {
      return deviceId === activeDevice;
    } else if (preferredDevice) {
      return deviceId === preferredDevice;
    } else {
      return index === 0;
    }
  });

  function onButtonClick() {
    if (status === "off") {
      start(selected?.deviceId);
    }
    if (status === "on") {
      stop();
    }
  }

  function onDeviceSelect(deviceId: MediaDeviceInfo["deviceId"]) {
    start(deviceId);
  }

  const C = Content[type];

  return (
    <AVButtonWrapper>
      <Button
        disabled={status === "starting" || status === "stopping"}
        onClick={onButtonClick}
        aria-label={on ? C.titleOff : C.titleOn}
        title={on ? C.titleOff : C.titleOn}
        icon={on ? C.iconOn : C.iconOff}
      ></Button>
      <Menu disabled={!deviceOptions} ariaLabel={C.menuAria} icon="Chevron">
        <MenuLabel>{C.menuLabel}</MenuLabel>
        {deviceOptions?.map(({ deviceId, label }) => {
          const checked = selected?.deviceId === deviceId;
          return (
            <MenuItem key={deviceId} onSelect={() => onDeviceSelect(deviceId)}>
              <Icon icon="Checkmark" style={{ opacity: checked ? 1 : 0 }} />
              {label}
            </MenuItem>
          );
        })}
      </Menu>
    </AVButtonWrapper>
  );
}

const Content = {
  video: {
    menuLabel: "Cameras",
    menuAria: "Select video input",
    iconOn: "CamOn",
    iconOff: "CamOff",
    titleOn: "Turn camera on",
    titleOff: "Turn camera off",
  },
  audio: {
    menuLabel: "Microphones",
    menuAria: "Select audio input",
    iconOn: "MicOn",
    iconOff: "MicOff",
    titleOn: "Unmute",
    titleOff: "Mute",
  },
};

const AVButtonWrapper = styled(SegmentedButtonWrapper, {
  [`& [aria-haspopup=menu]`]: {
    paddingTop: "5px",
  },
});
